import { useMemo, useRef, useState } from "react";
import { useApp } from "../lib/store";
import { exportCsv, api, errDetail } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Download, Upload, Search, Inbox } from "lucide-react";
import { toast } from "sonner";

export function DataTable({ module, rows, columns, onRowClick, toolbar, onImported, filters }) {
  const { t, plLabel } = useApp();
  const [q, setQ] = useState("");
  const [fvals, setFvals] = useState({});
  const fileRef = useRef(null);

  const filtered = useMemo(() => {
    let out = rows;
    for (const f of filters || []) {
      const v = fvals[f.key];
      if (v && v !== "__all__") out = out.filter((r) => (f.match ? f.match(r, v) : r[f.key] === v));
    }
    if (q) {
      const s = q.toLowerCase();
      out = out.filter((r) => JSON.stringify(r).toLowerCase().includes(s));
    }
    return out;
  }, [rows, q, filters, fvals]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(Boolean);
    const headers = lines[0].split(",").map((h) => h.replace(/^"|"$/g, "").trim());
    const parsed = lines.slice(1).map((line) => {
      const vals = line.match(/("([^"]|"")*"|[^,]*)(,|$)/g)?.map((v) => v.replace(/,$/, "").replace(/^"|"$/g, "").replace(/""/g, '"')) || [];
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] ?? ""; });
      return obj;
    });
    try {
      const r = await api.post(`/${module}/import`, { rows: parsed });
      toast.success(`${r.data.imported} ${t("records")} ✓`);
      onImported?.();
    } catch (err) {
      toast.error(errDetail(err));
    }
    e.target.value = "";
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input data-testid={`${module}-list-search`} className="pl-8 h-9" placeholder={`${t("search_placeholder")}`}
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <span className="text-xs text-gray-500 font-mono">{filtered.length} {t("records")}</span>
        {(filters || []).map((f) => (
          <select key={f.key} data-testid={`${module}-filter-${f.key}`}
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700"
            value={fvals[f.key] || "__all__"} onChange={(e) => setFvals((p) => ({ ...p, [f.key]: e.target.value }))}>
            <option value="__all__">{`${f.label}: ${t("all") || "All"}`}</option>
            {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <div className="flex-1" />
        {toolbar}
        <Button variant="outline" size="sm" data-testid={`${module}-export-btn`}
          onClick={() => exportCsv(filtered, module)}>
          <Download className="h-3.5 w-3.5 mr-1" />{t("export_csv")}
        </Button>
        <Button variant="outline" size="sm" data-testid={`${module}-import-btn`} onClick={() => fileRef.current?.click()}>
          <Upload className="h-3.5 w-3.5 mr-1" />{t("import_csv")}
        </Button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
      </div>
      {filtered.length === 0 ? (
        <div className="py-16 text-center" data-testid={`${module}-empty-state`}>
          <Inbox className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="font-medium text-gray-700">{t("empty_title")}</p>
          <p className="text-sm text-gray-400">{t("empty_cta")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                {columns.map((c) => (
                  <th key={c.key} className={`px-4 py-2.5 font-semibold ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`${module}-row`} onClick={() => onRowClick?.(r)}
                  className="border-b border-gray-100 hover:bg-amber-50/40 cursor-pointer transition-colors duration-150">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-2.5 ${c.align === "right" ? "text-right font-mono" : ""}`}>
                      {c.render ? c.render(r) : c.picklist ? plLabel(c.picklist, r[c.key]) ?? "–" : (r[c.key] ?? "–")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
